import { View } from "react-native";
import {
  Controller,
  Control,
  useWatch,
  UseFormSetValue,
} from "react-hook-form";
import { useEffect, useMemo } from "react";

import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { isNullOrUndefined, requiredValidation } from "ui/utils/Validators";
import { OtherInformationForm } from "app/services/api/types/residents/models/AddResidentOtherInformationForm";
import { OtherInformationCategory } from "app/services/api/types/residents/responses/OtherInformationCategoriesResponse";
import KeyValueSelector from "ui/organisms/KeyValueSelector";

interface CategoryItemProps {
  category: OtherInformationCategory;
  control: Control<OtherInformationForm>;
  setValue: UseFormSetValue<OtherInformationForm>;
}

export const OtherInfoCategoryItem = ({
  category,
  control,
  setValue,
}: CategoryItemProps) => {
  const fieldName = `${category.id}`;
  const watchedValue = useWatch({ control, name: `${fieldName}.value` });

  const requiresDescription = useMemo(() => {
    if (watchedValue === category.id) return category.requires_description;
    return category.children?.some(
      (child) => child.id === watchedValue && child.requires_description,
    );
  }, [watchedValue, category]);

  useEffect(() => {
    if (!requiresDescription) {
      setValue(`${fieldName}.description`, "");
    }
  }, [requiresDescription, fieldName, setValue]);

  return (
    <View className="flex flex-col gap-4">
      <Controller
        control={control}
        name={`${fieldName}.value`}
        rules={{
          validate: (value) =>
            value !== undefined || `${category.name} selection is required`,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const options = category.children?.length
            ? category.children.map(({ id, name }) => ({
                label: name,
                value: id,
              }))
            : [
                { label: "Yes", value: category.id },
                { label: "No", value: 0 },
              ];

          const selectedValues = !isNullOrUndefined(value) ? [value] : [];

          return (
            <KeyValueSelector<number | string>
              title={category.name}
              options={options}
              isMultiSelect={false}
              selectedValues={selectedValues}
              onChange={(values) => onChange(values?.[0])}
              errorMessage={error}
            />
          );
        }}
      />

      {requiresDescription && (
        <Controller
          control={control}
          name={`${fieldName}.description`}
          render={() => (
            <FormControlledInputTextArea
              id={`description-${fieldName}`}
              name={`${fieldName}.description`}
              control={control}
              placeholder={`Please specify ${category.name.toLowerCase()}`}
              isDisabled={!watchedValue}
              rules={{
                required: watchedValue
                  ? requiredValidation(category.name)
                  : undefined,
              }}
            />
          )}
        />
      )}
    </View>
  );
};
